import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import { Separator } from '@/shared/components/ui/separator';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import {
  useBoardGroups,
  useCreateBoardGroup,
  useUpdateBoardGroup,
  useDeleteBoardGroup,
} from '@/features/boards/public';
import type { BoardGroup } from '@/features/boards/public';

const GROUP_DOT_COLOR = '#94a3b8';

interface GroupPickerProps {
  boardId: string;
  projectId: string;
  currentGroupId?: string | null;
  onSelect: (groupId: string | null) => void;
  disabled?: boolean;
}

// ── Internal: option row ──────────────────────────────────────────────────────

interface GroupOptionRowProps {
  group: BoardGroup;
  isActive: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function GroupOptionRow({ group, isActive, onSelect, onEdit, onDelete }: GroupOptionRowProps) {
  const { t } = useTranslation('tasks');
  const [hovered, setHovered] = useState(false);

  return (
    <div
      data-testid={`tasks-group-picker-item-${group.id}`}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent',
        isActive && 'bg-accent/40',
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onSelect}
    >
      <span
        className="size-2 shrink-0 rounded-full"
        style={{ background: GROUP_DOT_COLOR }}
        aria-hidden
      />
      <span className={cn('flex-1 truncate text-xs', isActive && 'font-medium')}>{group.name}</span>
      {hovered && (
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                data-testid={`tasks-group-picker-item-edit-${group.id}`}
                onClick={onEdit}
                aria-label={t('groupPicker.editAria')}
              >
                <Pencil className="size-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('groupPicker.editAria')}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 hover:text-destructive"
                data-testid={`tasks-group-picker-item-delete-${group.id}`}
                onClick={onDelete}
                aria-label={t('groupPicker.deleteAria')}
              >
                <Trash2 className="size-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('groupPicker.deleteAria')}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

// ── Internal: inline edit row ─────────────────────────────────────────────────

interface GroupEditRowProps {
  group: BoardGroup;
  onSave: (name: string) => void;
  onCancel: () => void;
}

function GroupEditRow({ group, onSave, onCancel }: GroupEditRowProps) {
  const { t } = useTranslation('tasks');
  const [name, setName] = useState(group.name);

  return (
    <div className="rounded border bg-muted/30 px-2.5 py-2 mx-1 my-0.5">
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          data-testid={`tasks-group-picker-edit-input-${group.id}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && name.trim()) onSave(name.trim());
            if (e.key === 'Escape') onCancel();
          }}
          placeholder={t('groupPicker.namePlaceholder')}
          className="h-6 flex-1 text-xs"
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              data-testid={`tasks-group-picker-edit-save-${group.id}`}
              onClick={() => name.trim() && onSave(name.trim())}
              disabled={!name.trim()}
              aria-label={t('groupPicker.saveAria')}
            >
              <Check className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('groupPicker.saveAria')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              data-testid={`tasks-group-picker-edit-cancel-${group.id}`}
              onClick={onCancel}
              aria-label={t('groupPicker.cancelAria')}
            >
              <X className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('groupPicker.cancelAria')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Internal: create form ─────────────────────────────────────────────────────

interface GroupCreateFormProps {
  onSave: (name: string) => void;
  onCancel: () => void;
}

function GroupCreateForm({ onSave, onCancel }: GroupCreateFormProps) {
  const { t } = useTranslation('tasks');
  const [name, setName] = useState('');

  return (
    <div className="border-t px-2.5 pt-2.5 pb-2.5">
      <Input
        autoFocus
        data-testid="tasks-group-picker-create-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('groupPicker.namePlaceholder')}
        className="mb-2 h-7 text-xs"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) onSave(name.trim());
          else if (e.key === 'Escape') onCancel();
        }}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 flex-1 text-xs"
          data-testid="tasks-group-picker-create-cancel"
          onClick={onCancel}
        >
          {t('groupPicker.createCancel')}
        </Button>
        <Button
          size="sm"
          className="h-7 flex-1 text-xs"
          data-testid="tasks-group-picker-create-save"
          disabled={!name.trim()}
          onClick={() => name.trim() && onSave(name.trim())}
        >
          {t('groupPicker.createSave')}
        </Button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function GroupPicker({
  boardId,
  projectId,
  currentGroupId,
  onSelect,
  disabled,
}: GroupPickerProps) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

  const { groups } = useBoardGroups(boardId);
  const createGroup = useCreateBoardGroup(boardId);
  const updateGroup = useUpdateBoardGroup(boardId);
  const deleteGroup = useDeleteBoardGroup(boardId, projectId);
  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  const currentGroup = groups.find((g) => g.id === currentGroupId);

  const filtered = groups
    .filter((g) => g.id !== currentGroupId && g.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  function handleSelect(groupId: string | null) {
    onSelect(groupId);
    setOpen(false);
  }

  async function handleCreate(name: string) {
    await createGroup.mutateAsync({ name });
    setCreating(false);
  }

  function handleSaveEdit(groupId: string, name: string) {
    updateGroup.mutate({ groupId, data: { name } });
    setEditingGroupId(null);
  }

  async function handleDelete(group: BoardGroup) {
    const { confirmed } = await confirm({
      title: t('groupPicker.confirmDeleteTitle'),
      description: t('groupPicker.confirmDeleteDesc'),
      confirmLabel: t('groupPicker.confirmDeleteConfirm'),
      cancelLabel: t('groupPicker.confirmDeleteCancel'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    deleteGroup.mutate(group.id);
  }

  function handleOpenChange(v: boolean) {
    setOpen(v);
    if (!v) {
      setSearch('');
      setCreating(false);
      setEditingGroupId(null);
    }
  }

  const showActiveSection = !!currentGroupId && !!currentGroup && !search;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid="tasks-group-picker-trigger"
          className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ background: GROUP_DOT_COLOR }}
            aria-hidden
          />
          <span className={cn(currentGroup ? 'text-foreground' : 'text-muted-foreground')}>
            {currentGroup ? currentGroup.name : t('groupPicker.noGroup')}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-64 p-0"
        data-testid="tasks-group-picker-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 text-xs font-semibold">{t('groupPicker.title')}</div>
        <Separator />

        <div className="px-2 pt-2 pb-1">
          <Input
            data-testid="tasks-group-picker-search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setEditingGroupId(null);
            }}
            placeholder={t('groupPicker.searchPlaceholder')}
            className="h-7 text-xs"
          />
        </div>

        <div className="max-h-60 overflow-y-auto">
          {showActiveSection && (
            <>
              <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                {t('groupPicker.activeTitle')}
              </div>
              <div data-testid="tasks-group-picker-active-section" className="px-1 pb-1">
                {editingGroupId === currentGroup.id ? (
                  <GroupEditRow
                    group={currentGroup}
                    onSave={(name) => handleSaveEdit(currentGroup.id, name)}
                    onCancel={() => setEditingGroupId(null)}
                  />
                ) : (
                  <GroupOptionRow
                    group={currentGroup}
                    isActive
                    onSelect={() => handleSelect(null)}
                    onEdit={() => {
                      setEditingGroupId(currentGroup.id);
                      setCreating(false);
                    }}
                    onDelete={() => handleDelete(currentGroup)}
                  />
                )}
              </div>
              <Separator />
            </>
          )}

          <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('groupPicker.allTitle')}
          </div>
          <div data-testid="tasks-group-picker-all-section" className="px-1 pb-1">
            {filtered.map((group) =>
              editingGroupId === group.id ? (
                <GroupEditRow
                  key={group.id}
                  group={group}
                  onSave={(name) => handleSaveEdit(group.id, name)}
                  onCancel={() => setEditingGroupId(null)}
                />
              ) : (
                <GroupOptionRow
                  key={group.id}
                  group={group}
                  isActive={false}
                  onSelect={() => handleSelect(group.id)}
                  onEdit={() => {
                    setEditingGroupId(group.id);
                    setCreating(false);
                  }}
                  onDelete={() => handleDelete(group)}
                />
              ),
            )}
          </div>
        </div>

        {!creating ? (
          <button
            type="button"
            data-testid="tasks-group-picker-create-btn"
            className="flex w-full items-center gap-1.5 border-t px-3 py-2 text-xs text-primary hover:bg-accent"
            onClick={() => {
              setCreating(true);
              setEditingGroupId(null);
            }}
          >
            {t('groupPicker.createNew')}
          </button>
        ) : (
          <GroupCreateForm onSave={handleCreate} onCancel={() => setCreating(false)} />
        )}

        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(v) => {
            if (!v) handleCancel();
          }}
          title={confirmOptions?.title ?? ''}
          description={confirmOptions?.description}
          confirmLabel={confirmOptions?.confirmLabel}
          cancelLabel={confirmOptions?.cancelLabel}
          variant={confirmOptions?.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </PopoverContent>
    </Popover>
  );
}
